import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client.js';
import { useAuth } from '../../context/AuthContext.jsx';
import CubMascot from '../../components/CubMascot.jsx';
import { Loader, Alert, Stat, formatDate } from '../../components/ui.jsx';

/**
 * The student's own profile.
 *
 * Distinct from the account page on purpose: this is where a child sees
 * who they are and how far they have come, and the account page is
 * where they change things. So this one reads, and hands off to
 * /account for the two things that are actually settings — the cub and
 * the password.
 *
 * Everything here already existed behind the dashboard endpoint; the
 * profile is a place to show it as a person rather than a row of tiles
 * on the way to a lesson.
 */

/** A short, kid-legible "how long they have been here". */
const memberFor = (iso) => {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days < 1) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'}`;
  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? '' : 's'}`;
};

export default function Profile() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'My profile · CyberCub';
    api.studentDashboard().then(setData).catch((err) => setError(err.message));
  }, []);

  if (error) return <Alert kind="bad">{error}</Alert>;
  if (!data) return <Loader label="Opening your profile…" />;

  const { stats } = data;
  const earned = data.badges.filter((b) => b.awarded_at);
  const latest = [...earned].sort(
    (a, b) => new Date(b.awarded_at) - new Date(a.awarded_at),
  );
  const member = memberFor(user.memberSince);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>🐻 My profile</h1>
          <p>Who you are, and how far you have come.</p>
        </div>
        <Link to="/account" className="btn btn-ghost">⚙️ Edit cub &amp; password</Link>
      </div>

      {/* ---------- identity ---------- */}
      <section className="card profile-card">
        <div className="profile-face">
          <CubMascot avatar={user.avatar} mood="cheer" size={128} className="anim-float" />
          {stats?.rank != null && stats.rank <= 3 && (
            <span className="profile-crown" aria-label={`Rank ${stats.rank}`}>
              {stats.rank === 1 ? '🥇' : stats.rank === 2 ? '🥈' : '🥉'}
            </span>
          )}
        </div>
        <div className="profile-id">
          <h2>{user.fullName}</h2>
          <p className="profile-handle">{user.userId}</p>
          <ul className="profile-facts">
            <li>🏫 {user.schoolName ?? 'Learning at home'}</li>
            {user.age != null && <li>🎂 {user.age} years old</li>}
            {member && <li>🗓️ Member for {member}</li>}
            {stats?.rank != null ? (
              <li>🏆 Rank {stats.rank} of {stats.totalRanked}</li>
            ) : (
              <li>🏁 Finish a lesson to join the leaderboard</li>
            )}
          </ul>
        </div>
      </section>

      {/* ---------- progress ---------- */}
      {stats && (
        <div className="stat-grid mt-lg">
          <Stat icon="⭐" tone="sun" value={stats.xp} label="Total XP earned" />
          <Stat
            icon="📚"
            tone="grape"
            value={`${stats.lessonsCompleted}/${stats.totalLessons}`}
            label="Lessons completed"
          />
          <Stat
            icon="🏅"
            tone="mint"
            value={`${stats.badgesEarned}/${stats.badgesTotal}`}
            label="Badges collected"
          />
          <Stat icon="⏱" tone="sky" value={`${stats.minutesSpent} min`} label="Time learning" />
        </div>
      )}

      {/* ---------- badges ---------- */}
      <section className="mt-lg">
        <div className="page-head" style={{ marginBottom: '1rem' }}>
          <div>
            <h2 style={{ margin: 0 }}>🏅 Badges</h2>
            <p style={{ margin: '0.3rem 0 0' }}>
              {earned.length === 0
                ? 'None yet — your first lands the moment you pass a lesson quiz.'
                : `${earned.length} of ${data.badges.length} collected.`}
            </p>
          </div>
          <Link to="/learn/badges" className="btn btn-ghost btn-sm">See all →</Link>
        </div>

        {earned.length === 0 ? (
          <div className="card-flat profile-nobadges">
            <CubMascot avatar="cub-sky" mood="think" size={84} />
            <p className="muted">Finish your first lesson and it shows up right here.</p>
          </div>
        ) : (
          <div className="profile-badges">
            {latest.slice(0, 6).map((b) => (
              <div key={b.slug} className="profile-badge" title={b.description}>
                <span className="profile-badge-icon" aria-hidden="true">{b.icon}</span>
                <strong>{b.title}</strong>
                <span className="small faint">{formatDate(b.awarded_at)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ---------- recent ---------- */}
      {data.recent?.length > 0 && (
        <section className="mt-lg">
          <h2>📋 Recent quizzes</h2>
          <div className="card-flat">
            <ul className="login-history">
              {data.recent.map((r, i) => (
                <li key={i}>
                  <span>
                    <span aria-hidden="true">{r.icon}</span> {r.title}
                  </span>
                  <span className="muted">
                    {r.score}/{r.total} · {formatDate(r.created_at, true)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </>
  );
}
